import React, { useEffect, useState } from 'react';
import {
    TimelineParties,
    TimelineParty,
} from '../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import './EngagementHistoryTable.scss';
import moment from 'moment';
import InfoTooltip from '../info-tooltip/info-tooltip';
import { useStateSelector } from '../../store/selectors';
import createNotification from '../../utils/createNotification';
import { EngagementHistory } from '../../models/EngagementHistory/EngagementHistory';
import { EngagementHistoryRecord } from '../../models/EngagementHistory/EngagementHistoryRecord';
import SortingOption from '../Table/models/bootstrap-table-sorting-option';
import { ColumnDescription, SortOrder } from 'react-bootstrap-table-next';
import { sortingFunction } from '../Table/models/table-helper';
import SortingSymbol from '../SortingSymbol/sorting-symbol';
import Helpers from '../../utils/helper';
import { ReceiptPopup, ReceiptViewMode } from '../Receipts/ReceiptPopup';
import { isEventActionClickable } from '../../containers/ConnectMarketplaceDashboard/EventActionAvailabilityHelper';
import { TimelineEvent } from '../../slices/marketplace/timeline/models/timeline-event.model';
import { EngagementData } from '../../slices/marketplace/timeline/models/engagement-data.model';
import { EngagementBuyerActionState } from '../../slices/marketplace/timeline/models/engagement-buyer-action-state.model';
import { GetReceiptByDateRange } from '../GetReceiptByDateRange/GetReceiptByDateRange';
import { FormGroup, InputGroupAddon, Modal, ModalHeader, ModalBody } from 'reactstrap';
import { LoiSubmissionPackage } from '../../containers/Admin/BuyerMarketplace/OpportunityDetails/LoiSubmissionPackage/LoiSubmissionPackage';
import { useRef } from 'react';
import DatePicker from 'react-datepicker';
import { Input } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons';
import BuyerProfilePreviewPopup from '../BuyerProfilePreviewPopup/BuyerProfilePreviewPopup';
import {
    ActionsWithBuyerDocumentToSign,
    TimelineActionType,
} from '../../slices/marketplace/timeline/models/timeline-action-type.model';
import { DocumentDetailsStatus } from '../../slices/marketplace/timeline/models/enums/document-details-status.enum';
import { EngagementBuyer } from '../../slices/marketplace/timeline/models/engagement-buyer.model';
import { EngagementHistoryRecordFeeDetailTypes } from '../../models/EngagementHistory/EngagementHistoryRecordFeeDetailTypes.enum';
import { NotificationFlag } from '../NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../models/NotificationFlags/NotificationFlagsCategories';

interface EngagementHistoryTableProps {
    engagementId: number;
    events: TimelineEvent[];
    engagementData: EngagementData;
    viewParty: TimelineParty;
    onEventClick(eventId: string): void;
    isReadonly: boolean;
    refreshTrigger: boolean;
    currentBuyerActionState?: EngagementBuyerActionState[];
    buyers?: EngagementBuyer[];
    onAdminClickBuyerEvent?(eventId: string, buyerId: number): void;
    sellerId?: number;
    canNavigateToTransaction: boolean;
}

interface TableSortingState {
    data: EngagementHistoryRecord[];
    sortField: string;
    sortOrder: string;
}

const EngagementHistoryTable = (props: EngagementHistoryTableProps) => {
    const axios = useStateSelector((state) => state.core.axios);

    const [engagementHistoy, setEngagementHistoy] =
        useState<EngagementHistory>();
    const [timelineEventsList, setTimelineEventsList] = useState<
        EngagementHistoryRecord[]
    >([]);
    const [selectedTransactionId, setSelectedTransactionId] =
        useState<string>();
    const [selectedInvoicePayee, setSelectedInvoicePayee] = useState(null);
    const [isReceiptVisible, setIsReceiptVisible] = useState(false);
    const [isBuyerProfilePopupVisible, setIsBuyerProfilePopupVisible] =
        useState(false);
    const [selectedBuyerId, setSelectedBuyerId] = useState<number>(null);
    const [sortingOption, setSortingOption] = useState<SortingOption>({
        dataField: 'completionDate',
        order: 'asc',
    });
    const [execStartDate, setExecStartDate] = useState<Date | null>(null);
    const [execEndDate, setExecEndDate] = useState<Date | null>(null);
    const [deadlineStartDate, setDeadlineStartDate] = useState<Date | null>(
        null
    );
    const [deadlineEndDate, setDeadlineEndDate] = useState<Date | null>(null);
    const [eventFilter, setEventFilter] = useState('');
    const [partyFilter, setPartyFilter] = useState<number | ''>('');
    const [deliverableFilter, setDeliverableFilter] = useState('');
    const [feeFilter, setFeeFilter] = useState('');
    const [loiPreviewFormId, setLoiPreviewFormId] = useState<string | null>(null);

    const execStartRef = useRef<any>(null);
    const execEndRef = useRef<any>(null);
    const deadlineStartRef = useRef<any>(null);
    const deadlineEndRef = useRef<any>(null);

    useEffect(() => {
        fetchTableData();
    }, [props.refreshTrigger]);

    const getNestedValue = (obj: any, path: string): any => {
        return path.split('.').reduce((current, key) => {
            return current && current[key] !== undefined
                ? current[key]
                : undefined;
        }, obj);
    };

    const getEngagementUserIds = (): number[] => {
        const userIds =
            engagementHistoy?.timelineEvents.map((e) => e.userId) ?? [];
        const uniqueIds = Array.from(new Set(userIds));
        return uniqueIds;
    };

    const fetchTableData = () => {
        axios
            .get(`/api/Marketplace/${props.engagementId}/EngagementHistory`)
            .then((response) => {
                if (response.status !== 200) {
                    createNotification(
                        'An error occured on fetching Engagement History',
                        'error'
                    );
                    return;
                }

                setEngagementHistoy(response.data);
                updateTableData(
                    response.data.timelineEvents ?? [],
                    sortingOption
                );
            });
    };

    const openReceiptPopup = (transactionId: string, payeeId: number) => {
        setSelectedTransactionId(transactionId);
        setSelectedInvoicePayee(payeeId);
        setIsReceiptVisible(true);
    };

    const openBuyerProfile = (buyerId: number) => {
        setSelectedBuyerId(buyerId);
        setIsBuyerProfilePopupVisible(true);
    };

    const closeBuyerProfile = () => {
        setIsBuyerProfilePopupVisible(false);
        setSelectedBuyerId(null);
    };

    const isActionWithBuyerDocumentToComplete = (
        row: EngagementHistoryRecord
    ) => {
        const targetEvent = props.events.find((f) => f.id === row.eventId);

        const isBuyerSignDocumentAction =
            row.responsibleParty === TimelineParty.Buyer &&
            ActionsWithBuyerDocumentToSign.some(
                (actionType: TimelineActionType) =>
                    targetEvent?.linkedAction?.actionType === actionType
            );

        const isBuyerUploadDocumentAction =
            targetEvent?.linkedAction?.actionType ===
            TimelineActionType.BuyerUploadDocument;

        if (isBuyerSignDocumentAction || isBuyerUploadDocumentAction) {
            return true;
        }

        return false;
    };

    const isActionWithDuyberDocumentUpload = (row: EngagementHistoryRecord) => {
        const targetEvent = props.events.find((f) => f.id === row.eventId);

        const result =
            targetEvent.linkedAction?.actionType ===
            TimelineActionType.BuyerUploadDocument;
        return result;
    };

    const isBuyerDocumentCompleted = (row: EngagementHistoryRecord) => {
        if (
            (isActionWithBuyerDocumentToComplete(row) &&
                row.responsibleParty === TimelineParty.Buyer) ||
            isActionWithDuyberDocumentUpload(row)
        ) {
            const targetEvent = props.events.find((f) => f.id === row.eventId);
            const targetBuyer = props.buyers?.find(
                (b) => b.buyerId === row.buyerId
            );
            const targetDocumentId = targetBuyer?.actionState.find(
                (f) => f.actionId === targetEvent.linkedAction?.id
            )?.documentDetailsId;

            const targetDocument = props.engagementData.documentsList.find(
                (document) => document.id === targetDocumentId
            );

            if (
                targetDocument?.status === DocumentDetailsStatus.Completed &&
                targetDocument?.counterParty === TimelineParty.Buyer
            ) {
                return true;
            }
        }

        return false;
    };

    const renderDeliverableCell = (
        row: EngagementHistoryRecord,
        cellContent: string
    ): React.JSX.Element => {
        if (props.viewParty === TimelineParty.VetValue) {
            return renderDeliverableForAdmin(row, cellContent);
        }

        return renderDeliverableForUser(row, cellContent);
    };

    const renderFeeCell = (
        row: EngagementHistoryRecord,
        cellContent: string
    ): React.JSX.Element => {
        const transactionIds =
            row.feeDetails?.transactionIds?.filter((f) => f) || [];
        const uniqueTransactionIds = Array.from(new Set(transactionIds));
        const feeDetailTypesWithPaymentInfo = [
            EngagementHistoryRecordFeeDetailTypes.PaymentInfo,
            EngagementHistoryRecordFeeDetailTypes.Summary,
            EngagementHistoryRecordFeeDetailTypes.RefundInfo,
        ];

        const hasPaymentInfo = feeDetailTypesWithPaymentInfo.includes(
            row.feeDetails?.feeDetailsType
        );

        const eventCodes = uniqueTransactionIds.map(
            (transactionId) =>
                `${NotificationFlagsCategories.MpCode}.${props.engagementId}.${NotificationFlagsCategories.EngagementHistoryCode}.${NotificationFlagsCategories.EngagementAutoPaymentCode}.${row.userId}.${transactionId}`
        );
        const uniqueEventCodes = Array.from(new Set(eventCodes));

        return (
            <div className="d-flex align-items-center">
                <div>{cellContent ?? ''}</div>
                {hasPaymentInfo &&
                    uniqueEventCodes.map((eventCode) => {
                        const transactionId = eventCode.split('.').pop();
                        return (
                            <NotificationFlag
                                key={`${row.eventId}-${transactionId}`}
                                eventCode={eventCode}
                                includePrefix={false}
                            />
                        );
                    })}
            </div>
        );
    };

    const renderDeliverableForAdmin = (
        row: EngagementHistoryRecord,
        cellContent: string
    ): React.JSX.Element => {
        const isSignDocumentEventForBuyer =
            isActionWithBuyerDocumentToComplete(row);

        const targetBuyer = props.buyers?.find(
            (f) => f.buyerId === row.buyerId
        );

        const isLoiRecord = !!row.loiFormId;

        const isClickable = isLoiRecord
            ? true
            : isSignDocumentEventForBuyer && props.viewParty === TimelineParty.VetValue
            ? isBuyerDocumentCompleted(row)
            : isEventActionClickable(
                  row.eventId,
                  props.isReadonly,
                  props.engagementData,
                  props.events,
                  props.viewParty,
                  targetBuyer?.actionState ?? []
              );
        const eventClickHandler = () => {
            if (isLoiRecord) {
                setLoiPreviewFormId(row.loiFormId);
                return;
            }
            row.responsibleParty === TimelineParty.Buyer
                ? props.onAdminClickBuyerEvent(row.eventId, row.buyerId)
                : props.onEventClick(row.eventId);
        };
        return getDeliverableCellElement(
            cellContent,
            isClickable,
            eventClickHandler
        );
    };

    const renderDeliverableForUser = (
        row: EngagementHistoryRecord,
        cellContent: string
    ): React.JSX.Element => {
        if (row.loiFormId) {
            return getDeliverableCellElement(cellContent, true, () =>
                setLoiPreviewFormId(row.loiFormId)
            );
        }
        const isClickable = isEventActionClickable(
            row.eventId,
            props.isReadonly,
            props.engagementData,
            props.events,
            props.viewParty,
            props.currentBuyerActionState ?? []
        );
        return getDeliverableCellElement(cellContent, isClickable, () =>
            props.onEventClick(row.eventId)
        );
    };

    const getDeliverableCellElement = (
        cellContent: string,
        isClickable: boolean,
        eventClickHandler: () => void
    ) => (
        <div
            className={isClickable && 'link-text'}
            onClick={() => {
                if (isClickable) {
                    eventClickHandler();
                }
            }}>
            {cellContent}
        </div>
    );

    const getTimelineEventRecordColumns =
        (): ColumnDescription<EngagementHistoryRecord>[] => [
            { dataField: 'eventId', text: '', hidden: true },
            {
                dataField: 'completionDate',
                text: 'Completion Date',
                sort: true,
                formatter: (cellContent: Date) =>
                    cellContent ? moment(cellContent).format('L') : 'NA',
            },
            {
                dataField: 'deadlineDate',
                text: 'Original Deadline Date',
                sort: true,
                formatter: (cellContent: Date) =>
                    cellContent ? moment(cellContent).format('L') : 'NA',
            },
            {
                dataField: 'eventName',
                text: 'Event',
                sort: true,
            },
            {
                hidden: props.viewParty !== TimelineParty.VetValue,
                dataField: 'responsibleParty',
                text: 'Responsible Party',
                sort: true,
                formatter: (
                    cellContent: TimelineParty,
                    row: EngagementHistoryRecord
                ) => {
                    if (cellContent === TimelineParty.Seller) {
                        return (
                            TimelineParties.find((f) => f.id === cellContent)
                                ?.name ?? ''
                        );
                    }

                    return (
                        <div
                            className="link-text"
                            onClick={() => {
                                if (row.buyerId) {
                                    openBuyerProfile(row.buyerId);
                                }
                            }}>
                            {row.buyerName}
                        </div>
                    );
                },
            },
            {
                dataField: 'deliverableName',
                text: 'Deliverable',
                sort: true,
                formatter: (
                    cellContent: string,
                    row: EngagementHistoryRecord
                ) => {
                    return renderDeliverableCell(row, cellContent);
                },
            },
            {
                dataField: 'feeDetails.feeName',
                text: 'Fee',
                sort: true,
                formatter: (
                    cellContent: string,
                    row: EngagementHistoryRecord
                ) => {
                    return renderFeeCell(row, cellContent);
                },
            },
            {
                dataField: 'feeDetails.feeAmount',
                text: 'Fee Amount',
                sort: true,
                formatter: (
                    cellContent: number,
                    row: EngagementHistoryRecord
                ) => {
                    const isVisible =
                        row.feeDetails?.feeDetailsType ===
                            EngagementHistoryRecordFeeDetailTypes.PaymentInfo ||
                        row.feeDetails?.feeDetailsType ===
                            EngagementHistoryRecordFeeDetailTypes.Summary;

                    const isBrokerOrObserver =
                        props.viewParty === TimelineParty.Broker ||
                        props.viewParty === TimelineParty.Observer;

                    return isVisible ? (
                        <div
                            className={
                                props.isReadonly &&
                                props.viewParty !== TimelineParty.Broker &&
                                props.viewParty !== TimelineParty.Observer
                                    ? ''
                                    : 'link-text'
                            }
                            onClick={() => {
                                if (!props.isReadonly && !isBrokerOrObserver) {
                                    const transactionId =
                                        row.feeDetails?.transactionIds.find(
                                            (f) => f
                                        );
                                    openReceiptPopup(transactionId, row.userId);
                                }
                            }}>
                            {row.feeDetails?.isFeeAvailable &&
                            cellContent != null
                                ? Helpers.GetCurrencyFormat(cellContent)
                                : ''}
                        </div>
                    ) : (
                        <></>
                    );
                },
            },
            {
                hidden: props.viewParty == TimelineParty.Buyer,
                dataField: 'feeDetails.creditableAmount',
                text: 'Creditable Amount',
                sort: true,
                formatter: (
                    cellContent: number,
                    row: EngagementHistoryRecord
                ) => {
                    const isVisible =
                        row.feeDetails?.feeDetailsType ===
                            EngagementHistoryRecordFeeDetailTypes.PaymentInfo ||
                        EngagementHistoryRecordFeeDetailTypes.Summary;

                    return isVisible ? (
                        <div>
                            {row.feeDetails?.isFeeAvailable &&
                            cellContent != null &&
                            cellContent > 0
                                ? Helpers.GetCurrencyFormat(cellContent)
                                : ''}
                        </div>
                    ) : (
                        <></>
                    );
                },
            },
            {
                text: 'Refunds',
                dataField: 'feeDetails.refundAmount',
                headerFormatter: () => {
                    return (
                        <>
                            {'Refunds '}
                            <InfoTooltip
                                idText="engagement-history-refunds-header-tooltip"
                                text="Refunds are provided in the form of Store Credits, deposited directly into your wallet"></InfoTooltip>
                        </>
                    );
                },
                sort: true,
                formatter: (
                    cellContent: number,
                    row: EngagementHistoryRecord
                ) => {
                    const isVisible =
                        row.feeDetails?.feeDetailsType ===
                            EngagementHistoryRecordFeeDetailTypes.RefundInfo ||
                        row.feeDetails?.feeDetailsType ===
                            EngagementHistoryRecordFeeDetailTypes.Summary;

                    const isBrokerOrObserverRefunds =
                        props.viewParty === TimelineParty.Broker ||
                        props.viewParty === TimelineParty.Observer;

                    return isVisible ? (
                        <div
                            className="link-text"
                            onClick={() => {
                                if (!isBrokerOrObserverRefunds) {
                                    const transactionId =
                                        row.feeDetails?.transactionIds.find(
                                            (f) => f
                                        );
                                    const payeeId = row.userId;
                                    openReceiptPopup(transactionId, payeeId);
                                }
                            }}>
                            {row.feeDetails?.isFeeAvailable &&
                            cellContent != null
                                ? Helpers.GetCurrencyFormat(cellContent)
                                : ''}
                        </div>
                    ) : (
                        <></>
                    );
                },
            },
        ];

    const handleTableChange = (_type: any, newState: TableSortingState) => {
        const newSortingOption: SortingOption = {
            dataField: newState.sortField,
            order: newState.sortOrder as SortOrder,
        };
        setSortingOption(newSortingOption);
        updateTableData(newState.data, newSortingOption);
    };

    const updateTableData = (
        data: EngagementHistoryRecord[],
        sortingOption: SortingOption
    ) => {
        const countZeroAsEmpty = sortingOption.dataField.includes('feeDetails');
        const sortedHistory = sortingFunction(
            data,
            sortingOption,
            true,
            countZeroAsEmpty
        );
        setTimelineEventsList(sortedHistory);
    };

    const renderCellContent = (
        row: EngagementHistoryRecord,
        index: number,
        columnDefinition: ColumnDescription<EngagementHistoryRecord>
    ) => {
        const cellContent =
            getNestedValue(row, columnDefinition.dataField) ?? '';

        if (columnDefinition.formatter) {
            return (
                <td>{columnDefinition.formatter(cellContent, row, index)}</td>
            );
        }

        return <td>{cellContent.toString()}</td>;
    };

    const getTimelineEventsTableBody = () => {
        return (
            <tbody>
                {timelineEventsList.map(
                    (record: EngagementHistoryRecord, index) => {
                        return (
                            <tr key={index}>
                                {getTimelineEventRecordColumns()
                                    .filter((f) => !f.hidden)
                                    .map(
                                        (
                                            columnDefinition: ColumnDescription<EngagementHistoryRecord>
                                        ) =>
                                            renderCellContent(
                                                record,
                                                index,
                                                columnDefinition
                                            )
                                    )}
                            </tr>
                        );
                    }
                )}
            </tbody>
        );
    };

    const renderFilterBar = () => {
        const deliverableOptions = Array.from(
            new Set(
                (engagementHistoy?.timelineEvents ?? [])
                    .map((x: EngagementHistoryRecord) => x.deliverableName)
                    .filter((x) => x)
            )
        ) as string[];

        const feeOptions = Array.from(
            new Set(
                (engagementHistoy?.timelineEvents ?? [])
                    .map((x: EngagementHistoryRecord) => x.feeDetails?.feeName)
                    .filter((x) => x)
            )
        ) as string[];

        return (
            <div className="filters-bar">
                <FormGroup className="filter-scroller">
                    <div
                        onClick={() => {
                            execStartRef.current?.setOpen(true);
                        }}
                        className="filter-item">
                        <label>Completion Date Start</label>
                        <div className="d-flex small-border">
                            <InputGroupAddon
                                addonType="prepend"
                                onClick={() => {
                                    execStartRef.current?.setOpen(true);
                                }}>
                                <div className="input-group-text">
                                    <FontAwesomeIcon icon={faCalendarAlt} />
                                </div>
                            </InputGroupAddon>
                            <DatePicker
                                ref={execStartRef}
                                selected={execStartDate}
                                name="execStartDate"
                                dateFormat="MM/d/yyyy"
                                onChange={(v: Date) => handleExecStartChange(v)}
                                isClearable
                            />
                        </div>
                    </div>

                    <div className="dash-separator">{'-'}</div>

                    <div
                        onClick={() => {
                            execEndRef.current?.setOpen(true);
                        }}
                        className="filter-item">
                        <label>Completion Date End</label>
                        <div className="d-flex small-border">
                            <InputGroupAddon
                                addonType="prepend"
                                onClick={() => {
                                    execEndRef.current?.setOpen(true);
                                }}>
                                <div className="input-group-text">
                                    <FontAwesomeIcon icon={faCalendarAlt} />
                                </div>
                            </InputGroupAddon>
                            <DatePicker
                                ref={execEndRef}
                                selected={execEndDate}
                                name="execEndDate"
                                dateFormat="MM/d/yyyy"
                                onChange={(v: Date) => handleExecEndChange(v)}
                                isClearable
                            />
                        </div>
                    </div>

                    <div className="line-separator"></div>

                    <div
                        onClick={() => {
                            deadlineStartRef.current?.setOpen(true);
                        }}
                        className="filter-item">
                        <label>Deadline Date Start</label>
                        <div className="d-flex small-border">
                            <InputGroupAddon
                                addonType="prepend"
                                onClick={() => {
                                    deadlineStartRef.current?.setOpen(true);
                                }}>
                                <div className="input-group-text">
                                    <FontAwesomeIcon icon={faCalendarAlt} />
                                </div>
                            </InputGroupAddon>
                            <DatePicker
                                ref={deadlineStartRef}
                                selected={deadlineStartDate}
                                name="deadlineStartDate"
                                dateFormat="MM/d/yyyy"
                                onChange={(v: Date) =>
                                    handleDeadlineStartChange(v)
                                }
                                isClearable
                            />
                        </div>
                    </div>

                    <div className="dash-separator">{'-'}</div>

                    <div
                        onClick={() => {
                            deadlineEndRef.current?.setOpen(true);
                        }}
                        className="filter-item">
                        <label>Deadline Date End</label>
                        <div className="d-flex small-border">
                            <InputGroupAddon
                                addonType="prepend"
                                onClick={() => {
                                    deadlineEndRef.current?.setOpen(true);
                                }}>
                                <div className="input-group-text">
                                    <FontAwesomeIcon icon={faCalendarAlt} />
                                </div>
                            </InputGroupAddon>
                            <DatePicker
                                ref={deadlineEndRef}
                                selected={deadlineEndDate}
                                name="deadlineEndDate"
                                dateFormat="MM/d/yyyy"
                                onChange={(v: Date) =>
                                    handleDeadlineEndChange(v)
                                }
                                isClearable
                            />
                        </div>
                    </div>

                    <div className="line-separator"></div>

                    <div className="filter-item member-input">
                        <label>Event</label>
                        <Input
                            value={eventFilter}
                            type="text"
                            onChange={(e) =>
                                handleEventFilterChange(e.target.value)
                            }
                        />
                    </div>

                    {props.viewParty === TimelineParty.VetValue ? (
                        <>
                            <div className="line-separator"></div>
                            <div className="filter-item">
                                <label>Responsible Party</label>
                                <div className="customerStyle">
                                    <select
                                        className="selectCustomer"
                                        value={partyFilter}
                                        onChange={(e) =>
                                            handlePartyFilterChange(
                                                e.target.value
                                            )
                                        }>
                                        <option value="">All</option>
                                        {TimelineParties.map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </>
                    ) : (
                        <></>
                    )}

                    <div className="line-separator"></div>

                    <div className="filter-item">
                        <label>Deliverable</label>
                        <div className="customerStyle">
                            <select
                                className="selectCustomer"
                                value={deliverableFilter}
                                onChange={(e) =>
                                    handleDeliverableFilterChange(
                                        e.target.value
                                    )
                                }>
                                <option value="">All</option>
                                {deliverableOptions.map((d) => (
                                    <option key={d} value={d}>
                                        {d}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="line-separator"></div>

                    <div className="filter-item">
                        <label>Fee</label>
                        <div className="customerStyle">
                            <select
                                className="selectCustomer"
                                value={feeFilter}
                                onChange={(e) =>
                                    handleFeeFilterChange(e.target.value)
                                }>
                                <option value="">All</option>
                                {feeOptions.map((f) => (
                                    <option key={f} value={f}>
                                        {f}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </FormGroup>
            </div>
        );
    };

    const applyFilters = () => {
        const source = engagementHistoy?.timelineEvents ?? [];
        const filtered = source.filter((r: EngagementHistoryRecord) => {
            const ed = r.completionDate ? new Date(r.completionDate) : null;
            const dd = r.deadlineDate ? new Date(r.deadlineDate) : null;

            if (execStartDate && (!ed || ed < new Date(execStartDate)))
                return false;
            if (execEndDate) {
                const e = new Date(execEndDate);
                e.setHours(23);
                e.setMinutes(59);
                e.setSeconds(59);
                e.setMilliseconds(999);
                if (!ed || ed > e) return false;
            }

            if (deadlineStartDate && (!dd || dd < new Date(deadlineStartDate)))
                return false;
            if (deadlineEndDate) {
                const d = new Date(deadlineEndDate);
                d.setHours(23);
                d.setMinutes(59);
                d.setSeconds(59);
                d.setMilliseconds(999);
                if (!dd || dd > d) return false;
            }

            if (
                eventFilter &&
                !(r.eventName ?? '')
                    .toLowerCase()
                    .includes(eventFilter.toLowerCase())
            )
                return false;

            if (partyFilter !== '' && r.responsibleParty !== partyFilter)
                return false;

            if (
                deliverableFilter &&
                (r.deliverableName ?? '') !== deliverableFilter
            )
                return false;

            if (feeFilter) {
                const name = r.feeDetails?.feeName ?? '';
                if (name !== feeFilter) return false;
            }

            return true;
        });

        updateTableData(filtered, sortingOption);
    };

    useEffect(() => {
        applyFilters();
    }, [
        execStartDate,
        execEndDate,
        deadlineStartDate,
        deadlineEndDate,
        eventFilter,
        partyFilter,
        deliverableFilter,
        feeFilter,
        engagementHistoy,
        sortingOption,
    ]);

    const handleExecStartChange = (v: Date | null) => {
        if (v && execEndDate) {
            const a = new Date(v.getFullYear(), v.getMonth(), v.getDate());
            const b = new Date(
                execEndDate.getFullYear(),
                execEndDate.getMonth(),
                execEndDate.getDate()
            );
            if (a > b) {
                const newEnd = new Date(v);
                newEnd.setHours(23);
                newEnd.setMinutes(59);
                setExecStartDate(execEndDate);
                setExecEndDate(newEnd);
                return;
            }
        }
        setExecStartDate(v);
    };

    const handleExecEndChange = (v: Date | null) => {
        let end = v ? new Date(v) : null;
        if (end) {
            end.setHours(23);
            end.setMinutes(59);
        }
        if (end && execStartDate) {
            const a = new Date(
                execStartDate.getFullYear(),
                execStartDate.getMonth(),
                execStartDate.getDate()
            );
            const b = new Date(
                end.getFullYear(),
                end.getMonth(),
                end.getDate()
            );
            if (a > b) {
                const newEnd = execStartDate ? new Date(execStartDate) : null;
                if (newEnd) {
                    newEnd.setHours(23);
                    newEnd.setMinutes(59);
                }
                setExecStartDate(end);
                setExecEndDate(newEnd);
                return;
            }
        }
        setExecEndDate(end);
    };

    const handleDeadlineStartChange = (v: Date | null) => {
        if (v && deadlineEndDate) {
            const a = new Date(v.getFullYear(), v.getMonth(), v.getDate());
            const b = new Date(
                deadlineEndDate.getFullYear(),
                deadlineEndDate.getMonth(),
                deadlineEndDate.getDate()
            );
            if (a > b) {
                const newEnd = new Date(v);
                newEnd.setHours(23);
                newEnd.setMinutes(59);
                setDeadlineStartDate(deadlineEndDate);
                setDeadlineEndDate(newEnd);
                return;
            }
        }
        setDeadlineStartDate(v);
    };

    const handleDeadlineEndChange = (v: Date | null) => {
        let end = v ? new Date(v) : null;
        if (end) {
            end.setHours(23);
            end.setMinutes(59);
        }
        if (end && deadlineStartDate) {
            const a = new Date(
                deadlineStartDate.getFullYear(),
                deadlineStartDate.getMonth(),
                deadlineStartDate.getDate()
            );
            const b = new Date(
                end.getFullYear(),
                end.getMonth(),
                end.getDate()
            );
            if (a > b) {
                const newEnd = deadlineStartDate
                    ? new Date(deadlineStartDate)
                    : null;
                if (newEnd) {
                    newEnd.setHours(23);
                    newEnd.setMinutes(59);
                }
                setDeadlineStartDate(end);
                setDeadlineEndDate(newEnd);
                return;
            }
        }
        setDeadlineEndDate(end);
    };

    const handleEventFilterChange = (v: string) => {
        setEventFilter(v);
    };

    const handlePartyFilterChange = (v: string) => {
        setPartyFilter(v === '' ? '' : Number(v));
    };

    const handleDeliverableFilterChange = (v: string) => {
        setDeliverableFilter(v);
    };

    const handleFeeFilterChange = (v: string) => {
        setFeeFilter(v);
    };

    return (
        <div className="engagement-history-table">
            <GetReceiptByDateRange
                isAdmin={props.viewParty === TimelineParty.VetValue}
                userIds={getEngagementUserIds()}
                engagementId={props.engagementId}
            />
            {renderFilterBar()}

            <div className="table-container">
                <table className="table">
                    <thead>
                        <tr>
                            {getTimelineEventRecordColumns()
                                .filter((f) => !f.hidden)
                                .map((columnDefinition: ColumnDescription) => {
                                    const columnSortOrder =
                                        sortingOption.dataField ===
                                        columnDefinition.dataField
                                            ? sortingOption.order
                                            : undefined;
                                    return (
                                        <th
                                            className={
                                                columnDefinition.sort
                                                    ? 'sortable'
                                                    : ''
                                            }
                                            onClick={() => {
                                                const tableChangeState: TableSortingState =
                                                    {
                                                        data: timelineEventsList,
                                                        sortField:
                                                            columnDefinition.dataField,
                                                        sortOrder:
                                                            columnSortOrder ===
                                                            'asc'
                                                                ? 'desc'
                                                                : 'asc',
                                                    };
                                                handleTableChange(
                                                    'sort',
                                                    tableChangeState
                                                );
                                            }}>
                                            {columnDefinition.headerFormatter
                                                ? columnDefinition.headerFormatter(
                                                      columnDefinition,
                                                      null,
                                                      null
                                                  )
                                                : `${columnDefinition.text}`}
                                            <span>
                                                <SortingSymbol
                                                    order={columnSortOrder}
                                                />
                                            </span>
                                        </th>
                                    );
                                })}
                        </tr>
                    </thead>
                    {getTimelineEventsTableBody()}
                </table>
            </div>

            {isReceiptVisible && (
                <ReceiptPopup
                    canNavigateToTransaction={props.canNavigateToTransaction}
                    isVisible={isReceiptVisible}
                    onClose={() => {
                        setIsReceiptVisible(false);
                    }}
                    mode={ReceiptViewMode.TransactionView}
                    transactionId={selectedTransactionId}
                    userId={selectedInvoicePayee}
                    isThirdPartyView={
                        props.viewParty === TimelineParty.VetValue ||
                        props.viewParty === TimelineParty.Broker ||
                        props.viewParty === TimelineParty.Observer
                    }
                />
            )}

            {isBuyerProfilePopupVisible ? (
                <BuyerProfilePreviewPopup
                    buyerId={selectedBuyerId}
                    close={closeBuyerProfile}
                />
            ) : (
                <></>
            )}

            {loiPreviewFormId && (
                <Modal
                    isOpen={!!loiPreviewFormId}
                    toggle={() => setLoiPreviewFormId(null)}
                    size="xl"
                    className="loi-preview-modal">
                    <ModalHeader toggle={() => setLoiPreviewFormId(null)}>
                        LOI Form Preview
                    </ModalHeader>
                    <ModalBody>
                        <LoiSubmissionPackage
                            loiFormId={loiPreviewFormId}
                            isReadonly={true}
                            canSubmit={false}
                            engagementId={props.engagementId}
                        />
                    </ModalBody>
                </Modal>
            )}
        </div>
    );
};

export default EngagementHistoryTable;
